import React, {
	memo,
	useRef,
	RefObject,
	ChangeEvent,
	useState,
	useCallback,
} from 'react'
import Button from '@material-ui/core/Button'
import { useTranslation } from 'react-i18next'
import PictureIcon from '@material-ui/icons/AddAPhotoOutlined'
import { makeStyles } from '@material-ui/styles'
import axios from 'axios'
import cuid from 'cuid'
import ReactPlayer from 'react-player'
import Chip from '@material-ui/core/Chip'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'

import { useArticleState } from '../../../../reducer'
import { StoreValue } from 'store'
import { buildArticleImage, SIGN_REQUEST_URIS } from 'utils'
import VideoUrl from '../videoUrl'
import {
	EArticleActionTypes,
	EArticleSectionTypes,
	IFeaturedMediaProps,
} from 'models'
import styles from './styles'
const useStyles = makeStyles(styles)

const FeaturedMediaView: React.FunctionComponent<IFeaturedMediaProps> = ({
	storeUserId,
}) => {
	const uploadInput: RefObject<HTMLInputElement> = useRef(null)
	const classes = useStyles()
	const { t } = useTranslation()
	const [state, dispatch] = useArticleState()
	const { id, section, error, featuredImage, featuredVideo } = state
	const deleteMedia = useCallback(() => {
		dispatch({
			type: EArticleActionTypes.clearMedia,
		})
		setStage(0)
	}, [dispatch])
	const [stage, setStage] = useState(featuredImage || featuredVideo ? 4 : 0)
	const [percent, update] = useState(0)
	const [fileId, setFileId] = useState(
		(featuredImage && featuredImage.id) || '',
	)
	const fileExtension = '-article-original.jpg'
	const triggerFileSelect = useCallback(() => {
		if (uploadInput && uploadInput.current) uploadInput.current.click()
	}, [uploadInput])

	const handleFileSelect = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			setStage(1)
			const source: any =
				uploadInput &&
				uploadInput.current &&
				uploadInput.current.files &&
				uploadInput.current.files[0]
			const blob = source.slice(0, source.size, source.type)
			const fileId = cuid()
			setFileId(fileId)
			const file = new File([blob], `${fileId}${fileExtension}`, {
				type: source.type,
			})
			const params = {
				userId: storeUserId,
				fileName: file && file.name,
				contentType: file && file.type,
				environment: process.env.REACT_APP_ENVIRONMENT,
				id,
				type: 'article',
			}

			axios({
				method: 'post',
				//@ts-ignore
				url: SIGN_REQUEST_URIS[process.env.REACT_APP_ENVIRONMENT],
				data: params,
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
				.then(({ data: { signedUrl } }: any) => {
					setStage(3)
					var options = {
						headers: {
							'Content-Type': file && file.type,
						},
						onUploadProgress: (progressEvent: any) => {
							let newPercent: any =
								(progressEvent.loaded / progressEvent.total) *
								100
							if (newPercent > 100) newPercent = 100
							update(newPercent.toFixed(0))
						},
					}
					axios
						.put(signedUrl, file, options)
						.then(() => {
							dispatch({
								type: EArticleActionTypes.setFeaturedImage,
								featuredImage: {
									id: fileId,
									isFeatured: true,
									filename: fileId + fileExtension,
								},
							})
							setStage(4)
						})
						.catch(error => {
							console.error(error)
						})
				})
				.catch(error => {
					setStage(2)
					console.error(error)
				})
		},
		[dispatch, id, storeUserId],
	)
	let buttonText: any = ''
	switch (stage) {
		case 1:
			buttonText = t('Preparing ...')
			break
		case 2:
			buttonText = t('Something went wrong ... try again')
			break
		case 3:
			buttonText = t('Uploading ... ') + percent + '%'
			break
		default:
			buttonText = <PictureIcon style={{ width: 50, height: 50 }} />
	}

	return (
		<div
			className={classes.featuredMediaContainer}
			style={
				section === EArticleSectionTypes.article &&
				(featuredImage || featuredVideo)
					? { borderColor: 'rgba(0,0,0,0.2)' }
					: {}
			}
		>
			{featuredVideo && (
				<>
					<Chip
						onClick={deleteMedia}
						style={{ background: '#fff' }}
						icon={<DeleteOutlinedIcon />}
						variant="outlined"
						color={'secondary'}
						clickable={true}
						classes={{
							label: classes.deleteIconLabel,
							root: classes.deleteRootElement,
						}}
					/>
					<ReactPlayer
						url={featuredVideo}
						style={{ flex: 1, overflow: 'hidden' }}
						className={classes.player}
						controls={true}
					/>
				</>
			)}
			{!featuredVideo &&
				(stage !== 4 ? (
					<>
						<input
							onChange={handleFileSelect}
							ref={uploadInput}
							type="file"
							accept=" .jpg,.jpeg,.png,.tiff"
							style={{ display: 'none' }}
						/>
						<div className={classes.uploadMediaButtons}>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
								}}
							>
								<Button
									color="primary"
									onClick={triggerFileSelect}
								>
									{buttonText}
								</Button>
								{stage === 0 && <p>upload pic</p>}
							</div>

							{stage === 0 && <VideoUrl />}
						</div>
						{section === 'article' &&
							(error.featuredImage || error.featuredVideo) && (
								<div className={classes.uploadMediaErrorMsg}>
									{t('Add an image or a video (link).')}
								</div>
							)}
					</>
				) : (
					<>
						<Chip
							onClick={deleteMedia}
							style={{ background: '#fff' }}
							icon={<DeleteOutlinedIcon />}
							variant="outlined"
							color={'secondary'}
							clickable={true}
							classes={{
								label: classes.deleteIconLabel,
								root: classes.deleteRootElement,
							}}
						/>
						<img
							style={{
								maxWidth: 300,
								maxHeight: 150,
								alignSelf: 'center',
							}}
							src={buildArticleImage(
								id,
								`${fileId}${fileExtension}`,
							)}
							alt=""
						/>
					</>
				))}
		</div>
	)
}

const FeatureMedia = memo(() => (
	<StoreValue keys={['user.id|storeUserId']}>
		<FeaturedMediaView />
	</StoreValue>
))

export default FeatureMedia
